/**
 * zugang.ts — Wer ist angemeldet, und in wessen Namen arbeitet er?
 *
 * DRPX-Pro (App-Teil Lieferung 1, 16.10.2026, „Weg B"): Ein Mitarbeiter
 * (Rolle `mitarbeiter`) arbeitet in der Oberfläche als seine FIRMA. Beim
 * Anmelden baut `baueMitarbeiterSicht` deshalb ein Benutzer-Objekt, dessen
 * `id` die Firmen-ID ist (Daten aus `get_meine_firma()`, Migration 139). Alles,
 * was „die Firma" meint — Aufträge anlegen, bieten, Upload-Pfade, Live-Filter,
 * Listen — stimmt damit ohne Änderung. Die PERSON steckt in `personId`.
 *
 * Regel für neuen Code:
 *  - Geht es um die Firma als Ganzes (Aufträge, Gebote, Fuhrpark …)?
 *    → `user.id` und `istFirmenZugang(user)`.
 *  - Geht es um den Menschen am Gerät (Chat-Absender, eigenes Profil,
 *    Benachrichtigungen, „ausgeblendet")? → `personId(user)`.
 *  - Ist es Chefsache (Abo, Firmen-/Bankdaten, Verifizierung, Zertifikate
 *    hochladen, Rote Liste, Konto löschen)? → `istChef(user)`.
 *
 * Die Rolle bleibt bewusst `mitarbeiter` (Entscheidung 15.10.2026): Eine
 * Stelle, die noch streng auf `'firma'` prüft, bleibt für Mitarbeiter
 * gesperrt statt still falsch.
 *
 * @module lib/zugang
 */

import type { Profile, TeamRechte } from '../types.js';

/** Die Felder, die die Prüfungen lesen. Echte `Profile` passen (duck typing). */
interface ZugangUser {
  id?: string | null;
  rolle?: string | null;
  personId?: string | null;
  darfBieten?: boolean | null;
  firma?: string | null;
  vorname?: string | null;
  nachname?: string | null;
  aboTier?: string | null;
  teamRechte?: TeamRechte | null;
}

type U = ZugangUser | null | undefined;

/** Mitarbeiter-Zugang einer Firma (DRPX-Pro)? */
export function istMitarbeiter(u: U): boolean {
  return !!u && u.rolle === 'mitarbeiter';
}

/** Firmenkonto selbst (der Chef)? Für Chef-Bereiche. */
export function istChef(u: U): boolean {
  return !!u && u.rolle === 'firma';
}

/** Arbeitet der Zugang für eine Firma — Chef ODER Mitarbeiter? */
export function istFirmenZugang(u: U): boolean {
  return istChef(u) || istMitarbeiter(u);
}

/**
 * Darf der Zugang im Marktplatz bieten (Rolle, nicht Abo)? Fahrer immer,
 * Firma/Mitarbeiter nur mit `darfBieten` der Firma (Migration 105/106).
 */
export function istBieter(u: U): boolean {
  if (!u) return false;
  return u.rolle === 'fahrer' || (istFirmenZugang(u) && u.darfBieten === true);
}

/** Ein Recht, das der Chef einem Mitarbeiter gibt (Migration 141). */
export type TeamRecht = keyof TeamRechte;

/**
 * Darf der Zugang das? Firma, Fahrer, Admin: immer (dort entscheiden Rolle,
 * Abo usw. wie bisher). Mitarbeiter: laut den Rechten vom Chef. Fehlen die
 * Rechte (nicht geladen), gilt „nein" — Zuschlag und Bieten prüft die
 * Datenbank ohnehin selbst (Migration 141), „Abrechnung sehen" nur die App.
 */
export function darf(u: U, recht: TeamRecht): boolean {
  if (!u) return false;
  if (!istMitarbeiter(u)) return true;
  return !!u.teamRechte && u.teamRechte[recht] === true;
}

/** Sind zwei Rechte-Stände gleich? (null/undefined = „nicht geladen") */
export function rechteGleich(a: TeamRechte | null | undefined, b: TeamRechte | null | undefined): boolean {
  if (!a || !b) return !a && !b;
  return a.zuschlag === b.zuschlag && a.bieten === b.bieten && a.abrechnung === b.abrechnung;
}

/**
 * Darf der Zugang sein Team verwalten? Nur der Chef einer Firma mit DRPX-Pro.
 * (Den Menüpunkt „Team“ sieht seit Lieferung 3a jeder Chef — ohne Pro steht
 * dort das Pro-Angebot.)
 */
export function hatTeamSeite(u: U): boolean {
  return istChef(u) && u?.aboTier === 'pro';
}

/**
 * Zeigt die App „Angelegt von" und den Filter „Nur von mir angelegt"?
 * Nur im Team: Chef mit DRPX-Pro oder (aktiver) Mitarbeiter — ein
 * Mitarbeiter kommt ohnehin nur bei einer Firma mit Pro hinein. Bei einer
 * Firma ohne Team hat alles der Chef angelegt; die Zeile wäre überflüssig.
 */
export function siehtUrheber(u: U): boolean {
  return hatTeamSeite(u) || istMitarbeiter(u);
}

/**
 * Seite des Zugangs im Auftrags-Chat. Mitarbeiter schreiben für ihre Firma
 * und stehen damit auf derselben Seite wie der Chef. Admin/unbekannt → null.
 */
export function chatSeite(u: U): 'firma' | 'fahrer' | null {
  if (istFirmenZugang(u)) return 'firma';
  if (u && u.rolle === 'fahrer') return 'fahrer';
  return null;
}

/**
 * ID des Menschen am Gerät (= `auth.uid()`). Beim Mitarbeiter die eigene
 * Konto-ID, bei allen anderen dieselbe wie `user.id`.
 */
export function personId(u: U): string {
  if (!u) return '';
  return u.personId || u.id || '';
}

/** Vor- und Nachname der Person (leer, wenn nicht eingetragen). */
export function personName(u: U): string {
  if (!u) return '';
  return [u.vorname, u.nachname].map((s) => (s || '').trim()).filter(Boolean).join(' ');
}

/**
 * Stammt eine Nachricht (oder ein „tippt gerade") von mir oder dem
 * Firmenkonto? Rückfall, wenn die Seite unbekannt ist (Nachrichten ohne
 * `sender_seite`, z. B. vor Migration 140). Mit Seite: {@link istVonMeinerSeite}.
 */
export function istEigeneNachricht(u: U, senderId: string | null | undefined): boolean {
  if (!u || !senderId) return false;
  return senderId === personId(u) || senderId === u.id;
}

/**
 * Meine Seite in einem Auftrag: 'firma' = Auftraggeber (`firmaId`),
 * 'fahrer' = Auftragnehmer (`fahrerId`). Richtet sich nach dem AUFTRAG, nicht
 * nach der Rolle — eine Spedition kann beides sein (17.10.2026; vorher galt
 * jede Firma als Auftraggeber, auch wenn sie den Auftrag fuhr).
 *
 * @returns Seite oder null, wenn ich nicht beteiligt bin
 */
export function meineAuftragsSeite(
  u: U,
  firmaId: string | null | undefined,
  fahrerId: string | null | undefined,
): 'firma' | 'fahrer' | null {
  if (!u || !u.id) return null;
  if (firmaId && firmaId === u.id) return 'firma';
  if (fahrerId && fahrerId === u.id) return 'fahrer';
  return null;
}

/**
 * Steht eine Chat-Nachricht auf meiner Seite? Mit `senderSeite` (Migration
 * 140) zählt die ganze Firma — Chef, ich und Kollegen. Ohne Seite Rückfall
 * auf {@link istEigeneNachricht}.
 */
export function istVonMeinerSeite(
  u: U,
  nachricht: { senderId?: string | null; senderSeite?: string | null },
  meineSeite: 'firma' | 'fahrer' | null,
): boolean {
  if (nachricht.senderSeite && meineSeite) return nachricht.senderSeite === meineSeite;
  return istEigeneNachricht(u, nachricht.senderId);
}

/**
 * Absendername im Chat, wie ihn die Datenbank setzt (Migration 135):
 * Mitarbeiter „Firmenname · Vor- und Nachname", sonst Firmenname.
 * Für Live-Anzeige und Push-Titel; gespeichert wird der Name der Datenbank.
 */
export function chatAbsenderName(u: (ZugangUser & { name?: string; email?: string }) | null | undefined): string {
  if (!u) return '';
  if (istMitarbeiter(u)) {
    const firma = (u.firma || '').trim();
    const person = personName(u);
    if (firma && person) return firma + ' · ' + person;
    return firma || person;
  }
  return u.firma || u.name || u.email || '';
}

/**
 * Baut die Firmen-Sicht eines Mitarbeiters (Weg B).
 *
 * Von der FIRMA (aus `get_meine_firma()`): ID, Rolle-unabhängige Firmendaten,
 * Bieten-Recht, Verifizierungs-/Abo-Status, Einsatzgebiet, Favoriten, Rote
 * Liste, Transport-Anweisungen. Von der PERSON: E-Mail, Anrede, Vor-/Nachname,
 * Benachrichtigungs-Einstellungen, Admin-Nachrichten, zuletzt aktiv.
 *
 * Kein Inhaber, keine Kundennummer, keine Bank-/Steuerdaten — die liefert
 * die Datenbank einem Mitarbeiter gar nicht erst.
 *
 * @param person Eigenes Profil (Rolle `mitarbeiter`)
 * @param firma  Firmendaten aus `get_meine_firma()`
 * @param rechte Rechte vom Chef (`firma_mitglieder`, Migration 141); null =
 *               nicht geladen → {@link darf} sagt überall „nein"
 * @returns Benutzer-Objekt mit `id` = Firma, `personId` = Person, Rolle `mitarbeiter`
 */
export function baueMitarbeiterSicht(person: Profile, firma: Profile, rechte: TeamRechte | null = null): Profile {
  return {
    ...firma,
    id: firma.id,
    rolle: 'mitarbeiter',
    personId: person.id,
    email: person.email,
    anrede: person.anrede,
    vorname: person.vorname,
    nachname: person.nachname,
    notifPrefs: person.notifPrefs,
    adminMessages: person.adminMessages,
    lastActive: person.lastActive,
    inhaber: '',
    nutzerNr: undefined,
    aboTier: undefined,
    teamRechte: rechte || undefined,
  };
}

/**
 * i18n-Schlüssel für die Rollen-Anzeige im Kopf/Menü. Admin → null
 * (Anzeige „Admin" bleibt wie bisher fest).
 */
export function rollenLabelKey(u: U): 'firmaLabel' | 'fahrerLabel' | 'mitarbeiterLabel' | null {
  if (istChef(u)) return 'firmaLabel';
  if (istMitarbeiter(u)) return 'mitarbeiterLabel';
  if (u && u.rolle === 'fahrer') return 'fahrerLabel';
  return null;
}

/** Symbol zur Rolle im Kopf/Menü (Firma und Mitarbeiter: Gebäude). */
export function rollenIcon(u: U): 'building' | 'truck' | 'sparkle' {
  if (istFirmenZugang(u)) return 'building';
  if (u && u.rolle === 'fahrer') return 'truck';
  return 'sparkle';
}
